import { createContext, useContext, useEffect, useState } from 'react';

import { env } from '@/constant/env';
import { Locale } from '@/lib/i18n/dictionaries';

const locale = env.publicLocale as keyof typeof dictionaries;

const dictionaries = {
  en: () => import('./en.json').then((module) => module.default),
  'zh-cn': () => import('./cn.json').then((module) => module.default),
};

export async function getDictionary(locale: Locale) {
  return dictionaries[locale]();
}

type Dictionary = Awaited<ReturnType<typeof getDictionary>>;

export async function getTranslations(): Promise<Dictionary | null> {
  const dictionary = await getDictionary(locale);

  return dictionary || null;
}

export const ScraperTranslationsContext = createContext<{
  scraperTranslations: Dictionary | null;
  getTranslation: (id: string) => string | null;
}>({
  scraperTranslations: null,
  getTranslation: () => null,
});

export const useScraperTranslations = () => {
  return useContext(ScraperTranslationsContext);
};

export const getTranslation = async (id: string) => {
  return (await getDictionary(locale))[id as keyof Dictionary];
};

export const ScraperTranslationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [scraperTranslations, setScraperTranslations] = useState<Awaited<
    ReturnType<typeof getDictionary>
  > | null>(null);

  useEffect(() => {
    getTranslations().then((translations) => {
      setScraperTranslations(translations);
    });
  }, []);

  const getTranslation = (id: string) => {
    return scraperTranslations?.[id as keyof Dictionary] || null;
  };

  return (
    <ScraperTranslationsContext.Provider
      value={{ scraperTranslations, getTranslation }}
    >
      {children}
    </ScraperTranslationsContext.Provider>
  );
};
